import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, X, Shield, Plus, Trash2, Save, Lock, Bot, Globe } from "lucide-react";
import { rolesApi } from "../../lib/api";
import { getApiError } from "../../lib/utils";

/** Roles manager — CRUD for named permission bundles.
 *  System roles (SUPER_ADMIN / ADMIN / VIEWER) cannot be deleted, and their
 *  permission list stays authoritative (managed in code + auto-seeded).
 */
export default function RolesManagerModal({ onClose }) {
  const [roles, setRoles] = useState(null);
  const [taxonomy, setTaxonomy] = useState(null);
  const [editing, setEditing] = useState(null);   // active role being edited (custom only)
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null); // role to delete (opens reassignment dialog)

  const load = async () => {
    try {
      const [r, t] = await Promise.all([rolesApi.list(), rolesApi.taxonomy()]);
      setRoles(r || []); setTaxonomy(t);
    } catch (e) { toast.error(getApiError(e, "Load failed")); }
  };
  useEffect(() => { load(); }, []);

  const doDelete = (role) => {
    if (role.is_system) return;
    setDeleting(role);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" onClick={onClose} data-testid="roles-manager-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-md border border-slate-200 bg-white shadow-2xl flex flex-col">
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <div className="flex items-center gap-2">
              <Shield size={16} className="text-nmi-700" />
              <h3 className="font-heading text-lg font-bold text-slate-900">Manage roles</h3>
            </div>
            <p className="mt-0.5 text-xs text-slate-600">
              Create custom permission presets. System roles are locked.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCreating(true)}
              data-testid="create-role-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-nmi-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-nmi-800"
            >
              <Plus size={12} /> New role
            </button>
            <button onClick={onClose} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"><X size={18} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {!roles && <div className="p-10 text-center text-slate-500"><Loader2 size={18} className="inline animate-spin" /></div>}
          {roles && (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Key</th>
                  <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Label</th>
                  <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Web perms</th>
                  <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">TG perms</th>
                  <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Users</th>
                  <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Kind</th>
                  <th className="px-3 py-2 text-right text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((r) => (
                  <tr key={r.key} className="border-b border-slate-200 last:border-b-0" data-testid={`role-row-${r.key}`}>
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-800">{r.key}</td>
                    <td className="px-3 py-2 text-sm text-slate-900">{r.label}</td>
                    <td className="px-3 py-2 text-xs text-slate-700">{(r.permissions || []).length}</td>
                    <td className="px-3 py-2 text-xs text-slate-700">{(r.telegram_permissions || []).length}</td>
                    <td className="px-3 py-2 text-xs text-slate-700" data-testid={`role-user-count-${r.key}`}>
                      {r.user_count || 0}
                    </td>
                    <td className="px-3 py-2">
                      {r.is_system ? (
                        <span className="inline-flex items-center gap-1 rounded-sm border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-600">
                          <Lock size={9} /> System
                        </span>
                      ) : (
                        <span className="rounded-sm border border-nmi-200 bg-nmi-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-nmi-700">Custom</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1.5">
                        <button onClick={() => setEditing(r)} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:border-nmi-600 hover:text-nmi-700" data-testid={`role-edit-${r.key}`}>
                          Edit
                        </button>
                        {!r.is_system && (
                          <button onClick={() => doDelete(r)} className="rounded-md border border-red-200 bg-white px-2 py-1 text-[11px] font-semibold text-red-700 hover:bg-red-50" data-testid={`role-delete-${r.key}`}>
                            <Trash2 size={11} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {(creating || editing) && taxonomy && (
          <RoleEditor
            role={editing}
            taxonomy={taxonomy}
            onClose={() => { setCreating(false); setEditing(null); }}
            onSaved={() => { setCreating(false); setEditing(null); load(); }}
          />
        )}

        {deleting && (
          <DeleteRoleDialog
            role={deleting}
            allRoles={roles || []}
            onClose={() => setDeleting(null)}
            onDone={() => { setDeleting(null); load(); }}
          />
        )}
      </div>
    </div>
  );
}

function DeleteRoleDialog({ role, allRoles, onClose, onDone }) {
  const userCount = role.user_count || 0;
  const others = allRoles.filter((r) => r.key !== role.key);
  const defaultTarget = others.find((r) => r.key === "VIEWER")?.key || others[0]?.key || "";
  const [target, setTarget] = useState(defaultTarget);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (userCount > 0 && !target) {
      toast.error("Pick a role to reassign users to");
      return;
    }
    setBusy(true);
    try {
      const res = await rolesApi.delete(role.key, userCount > 0 ? target : undefined);
      const reassigned = res?.reassigned_users || 0;
      toast.success(
        reassigned > 0
          ? `Deleted ${role.label} · ${reassigned} user(s) reassigned to ${res.reassigned_to}`
          : `Deleted ${role.label}`,
      );
      onDone();
    } catch (e) {
      toast.error(getApiError(e, "Delete failed"));
    } finally { setBusy(false); }
  };

  return (
    <div
      className="absolute inset-0 z-20 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      onClick={() => !busy && onClose()}
      data-testid="delete-role-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md overflow-hidden rounded-md border border-red-200 bg-white shadow-2xl"
      >
        <div className="flex items-start gap-3 border-b border-slate-200 p-5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100">
            <Trash2 size={18} className="text-red-700" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-lg font-bold text-slate-900">
              Delete role "{role.label}"?
            </h3>
            <p className="mt-1 text-xs text-slate-600">
              {userCount > 0 ? (
                <>This role is in use by <b>{userCount}</b> user{userCount === 1 ? "" : "s"}. They must be reassigned to another role before we can delete it.</>
              ) : (
                <>No users carry this role — it's safe to delete.</>
              )}
            </p>
          </div>
        </div>

        {userCount > 0 && (
          <div className="p-5">
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
              Reassign {userCount} user{userCount === 1 ? "" : "s"} to
            </label>
            <select
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              disabled={busy}
              data-testid="delete-role-reassign-target"
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-nmi-600"
            >
              {others.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.label} ({(r.permissions || []).length} perms)
                </option>
              ))}
            </select>
            <p className="mt-2 text-[11px] text-slate-500">
              Users will inherit the target role's permissions immediately.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || (userCount > 0 && !target)}
            data-testid="delete-role-confirm"
            className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-50"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            {userCount > 0 ? `Reassign & delete` : `Delete role`}
          </button>
        </div>
      </div>
    </div>
  );
}

function RoleEditor({ role, taxonomy, onClose, onSaved }) {
  const isNew = !role;
  const [key, setKey] = useState(role?.key || "");
  const [label, setLabel] = useState(role?.label || "");
  const [enabled, setEnabled] = useState(new Set(role?.permissions || []));
  const [tgEnabled, setTgEnabled] = useState(new Set(role?.telegram_permissions || []));
  const [tab, setTab] = useState("web");  // "web" | "telegram"
  const [saving, setSaving] = useState(false);

  const toggle = (k) => setEnabled((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });
  const toggleTg = (k) => setTgEnabled((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  const groupedHsp = useMemo(() => {
    const g = {};
    (taxonomy.hsp_permissions || taxonomy.permissions || []).forEach((p) => { (g[p.feature] ||= []).push(p); });
    return g;
  }, [taxonomy]);
  const groupedTg = useMemo(() => {
    const g = {};
    (taxonomy.telegram_permissions || []).forEach((p) => { (g[p.feature] ||= []).push(p); });
    return g;
  }, [taxonomy]);

  const save = async () => {
    if (!label.trim()) { toast.error("Label is required"); return; }
    if (isNew && !key.trim()) { toast.error("Key is required"); return; }
    setSaving(true);
    try {
      const payload = {
        label: label.trim(),
        permissions: Array.from(enabled),
        telegram_permissions: Array.from(tgEnabled),
      };
      if (isNew) {
        await rolesApi.create({ key: key.trim(), ...payload });
        toast.success(`Created ${label}`);
      } else {
        const res = await rolesApi.update(role.key, payload);
        const affected = res?.affected_users || 0;
        toast.success(
          affected > 0
            ? `Updated ${label} · ${affected} user${affected === 1 ? "" : "s"} synced (web + bot)`
            : `Updated ${label}`,
        );
      }
      onSaved();
    } catch (e) { toast.error(getApiError(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const currentGrouped = tab === "web" ? groupedHsp : groupedTg;
  const currentEnabled = tab === "web" ? enabled : tgEnabled;
  const currentToggle = tab === "web" ? toggle : toggleTg;

  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-md border border-slate-200 bg-white shadow-2xl flex flex-col">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-nmi-700">{isNew ? "New role" : role.is_system ? "Edit system role" : "Edit role"}</div>
            <h4 className="font-heading text-lg font-bold text-slate-900">{isNew ? "Create custom role" : role.label}</h4>
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="border-b border-slate-200 bg-slate-50 px-6 py-3 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Key</label>
            <input value={key} onChange={(e) => setKey(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "_"))} disabled={!isNew} placeholder="OPS_MANAGER" data-testid="role-editor-key" className="w-full rounded-md border border-slate-200 bg-white px-2.5 py-1.5 font-mono text-xs outline-none focus:border-nmi-600 disabled:bg-slate-100 disabled:text-slate-500" />
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Label</label>
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ops Manager" data-testid="role-editor-label" className="w-full rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-nmi-600" />
          </div>
        </div>
        <div className="flex items-center gap-1 border-b border-slate-200 bg-white px-6 pt-3">
          <button
            data-testid="role-editor-tab-web"
            onClick={() => setTab("web")}
            className={`inline-flex items-center gap-1.5 rounded-t-md border border-b-0 px-3 py-1.5 text-xs font-semibold ${
              tab === "web" ? "border-slate-200 bg-white text-nmi-800" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <Globe size={12} /> Web · {enabled.size}
          </button>
          <button
            data-testid="role-editor-tab-telegram"
            onClick={() => setTab("telegram")}
            className={`inline-flex items-center gap-1.5 rounded-t-md border border-b-0 px-3 py-1.5 text-xs font-semibold ${
              tab === "telegram" ? "border-slate-200 bg-white text-nmi-800" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <Bot size={12} /> Telegram · {tgEnabled.size}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {Object.entries(currentGrouped).map(([feature, perms]) => (
            <div key={feature}>
              <div className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{feature}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                {perms.map((p) => {
                  const on = currentEnabled.has(p.key);
                  return (
                    <label key={p.key} className={`flex cursor-pointer items-start gap-2 rounded-md border px-2.5 py-1.5 ${on ? "border-nmi-600 bg-nmi-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                      <input type="checkbox" checked={on} onChange={() => currentToggle(p.key)} className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300 text-nmi-700 focus:ring-nmi-600" />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-semibold text-slate-900">{p.label}</div>
                        <code className="block truncate text-[10px] font-mono text-slate-400">{p.key}</code>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-6 py-3">
          <div className="text-xs text-slate-600">
            <b>{enabled.size}</b> web · <b>{tgEnabled.size}</b> telegram
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="role-editor-save" className="inline-flex items-center gap-1.5 rounded-md bg-nmi-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-nmi-800 disabled:opacity-60">
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
